import React, { createContext, useEffect, useState, useContext } from 'react'
import { useProductsApi } from '../api/products-api'

import { Product } from '../types'
import { useAccountDetailsContext } from './AccountDetailsProvider'

interface ProductsContextProps {
  selectedProduct: Product | undefined
  isProductsLoading: boolean
  hasProductsError: boolean
  products: Product[] | undefined
}

export const ProductsContext = createContext<ProductsContextProps | undefined>(
  undefined,
)

export const ProductsProvider: React.FC = ({ children }) => {
  const { accountDetails } = useAccountDetailsContext()
  const { fetchProducts } = useProductsApi()
  const [products, setProducts] = useState<Product[] | undefined>()
  const [selectedProduct, setSelectedProduct] = useState<Product | undefined>()
  const [isProductsLoading, setIsProductsLoading] = useState(false)
  const [hasProductsError, setHasProductsError] = useState(false)

  const getProducts = async (): Promise<void> => {
    setIsProductsLoading(true)
    setHasProductsError(false)
    try {
      const response = await fetchProducts()
      setProducts(response)
    } catch (err) {
      setHasProductsError(true)
    } finally {
      setIsProductsLoading(false)
    }
  }

  useEffect(() => {
    getProducts()
  }, [])

  useEffect(() => {
    if (accountDetails && products?.length) {
      const matchedProduct = products.find(
        product =>
          /* istanbul ignore next */
          product.productId === accountDetails.productId?.toString(),
      )
      setSelectedProduct(matchedProduct)
    }
  }, [accountDetails?.productId, products])

  return (
    <ProductsContext.Provider
      value={{ selectedProduct, isProductsLoading, hasProductsError, products }}
    >
      {children}
    </ProductsContext.Provider>
  )
}

export const useProductsContext = (): ProductsContextProps => {
  const context = useContext(ProductsContext)
  /* istanbul ignore next */
  if (context === undefined) {
    throw new Error('useProductsContext must be used within ProductsProvider')
  }
  return context
}
